import { useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useForm, Controller } from 'react-hook-form';
import { MDSButton } from '@marqvision/mds-v2';
import { Comment, AppInfo, ListingCommentPanelProps } from '../ListingComment/@types';
import { getDateString } from '../ListingComment/panel/@utils';
import { Textarea } from './TextArea';
import { FileChip, SimpleFileUploader } from './SimpleFileUploader';

const MIN_HEIGHT = 112;
const MAX_HEIGHT = 400;

const Styled = {
  Editor: styled.div`
    margin: 0 16px 16px;

    min-height: ${MIN_HEIGHT}px;
    max-height: ${MAX_HEIGHT}px;
    display: grid;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme?.comp?.input?.color?.border?.normal || '#e0e0e0'};
    transition: 0.2s;
    &:has(:focus-within) {
      border: 1px solid ${({ theme }) => theme?.comp?.input?.color?.border?.active || '#1976d2'};
      outline: 3px solid ${({ theme }) => theme?._raw_color?.blue200 || '#90caf9'};
    }
  `,

  TextareaWrapper: styled.div`
    display: grid;
  `,

  FileList: styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 4px 8px 0;
  `,

  PanelAction: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px;
  `,

  DialogAction: styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    padding: 0 16px 16px;
  `,
};

type Props = {
  appInfo: AppInfo & { ctaLabel?: string };
  apiHook: Pick<ListingCommentPanelProps['apiHook'], 'getPresignedUrl' | 'addComment'>;

  listingIds: number[];
  commentId?: number;
  isNewMessageLoading: boolean;
  onSendStart: (newMessage: Comment) => void;
  onSendSuccess: (response?: any) => void;
  onSendError?: (error?: any) => void;

  onChange?: (value: string) => void;
  onCancel?: () => void;
};

type FormData = {
  text: string;
  files: { file: File; isUploading: boolean; url: string }[];
};

export const SimpleTextEditor = (props: Props) => {
  const {
    appInfo,
    apiHook,
    onSendStart,
    onSendSuccess,
    onSendError,
    listingIds,
    commentId,
    isNewMessageLoading,
    onCancel,
  } = props;

  // todo-@jamie: [PROD-16671] 반드시 리팩토링!!! panel타입과 dialog 타입을 급하게 동시에 지원하느라 매우 이상한 분기문이 나왔다.

  const actionType = onCancel ? 'dialog' : 'panel';

  const addCommentMutation = apiHook.addComment();

  const { control, handleSubmit, setValue, getValues, watch, reset, formState } = useForm<FormData>({
    defaultValues: {
      text: '',
      files: [],
    },
    mode: 'onChange',
    resolver: async (values) => {
      const isValid = !!values.text || !!values.files.length;
      return {
        values: isValid ? values : {},
        errors: isValid ? {} : { root: { type: 'manual' } },
      };
    },
  });

  const handleCommentSubmit = handleSubmit(async (data) => {
    if (actionType === 'panel') {
      // 메시지 전송 이벤트에 대한 optimistic update ui
      const previewMessage: Comment = {
        id: Math.floor(Math.random() * 1000000),
        type: commentId ? 'reply' : 'comment',
        user: {
          email: appInfo.writerEmail,
          isAdmin: appInfo.isWriterAdmin,
        },
        replyCount: 0,
        text: data.text,
        createdAt: getDateString(new Date().toISOString()),
        files: data.files.map((f) => ({
          name: f.file.name,
          url: f.url,
        })),
      };
      onSendStart(previewMessage);

      // 메시지 내용은 클리어한다
      reset();
    }

    try {
      const res = await addCommentMutation.mutateAsync({
        attachments: data.files.map((f) => ({
          file_name: f.file.name,
          id: f.file.size,
          url: f.url,
        })),
        text: data.text,
        listingIds,
        parentId: commentId,
      });
      onSendSuccess(res);
    } catch (e) {
      console.error(e);
      onSendError?.(e);
    }
  });

  const fileListRef = useRef<HTMLDivElement>(null);
  const textAreaMaxHeight = useGetTextAreaHeight(fileListRef, watch('files'));

  const isUploadInProgress = watch('files').some((file) => file.isUploading);
  const isReadyToSubmit =
    !addCommentMutation.isLoading && !isUploadInProgress && formState.isValid && !isNewMessageLoading;

  return (
    <>
      <Styled.Editor>
        <Styled.TextareaWrapper>
          <Controller
            name="text"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Textarea
                value={value}
                onChange={(v) => {
                  onChange(v);
                  props.onChange?.(v);
                }}
                isDisabled={addCommentMutation.isLoading || isNewMessageLoading}
                // 아래 file list 영역만큼 max height를 보정한다
                maxHeight={textAreaMaxHeight}
              />
            )}
          />
        </Styled.TextareaWrapper>
        <Styled.FileList ref={fileListRef} className="file-list">
          {watch('files').map(({ file, isUploading }, index, currentFiles) => (
            <FileChip
              key={index}
              file={file}
              isUploading={isUploading}
              onDelete={() => {
                const newFiles = currentFiles.filter((_, i) => i !== index);
                setValue('files', newFiles, { shouldValidate: true });
              }}
            />
          ))}
        </Styled.FileList>
        <Styled.PanelAction>
          <SimpleFileUploader
            fileList={watch('files').map((f) => f.file.name)}
            isDisabled={isUploadInProgress || addCommentMutation.isLoading || isNewMessageLoading}
            onChange={async (files) => {
              const currentFiles = getValues('files') || [];
              const newFiles = files.map((f) => ({ file: f, isUploading: true, url: '' }));
              setValue('files', [...currentFiles, ...newFiles], { shouldValidate: true });

              // presigned url 요청
              const { urls } = await apiHook.getPresignedUrl(newFiles.map((f) => f.file.name));

              // presigned url 요청 결과를 통해 파일 업로드
              const result = await Promise.allSettled(
                urls.map((url, index) => {
                  if (url) {
                    return fetch(url, {
                      method: 'PUT',
                      body: newFiles[index].file,
                    });
                  }
                  return Promise.resolve();
                })
              );

              const updatedFiles = newFiles.map((f, index) => ({
                ...f,
                isUploading: false,
                url: result[index].status === 'fulfilled' && urls[index] ? urls[index] : '',
              }));

              setValue('files', [...currentFiles, ...updatedFiles], { shouldValidate: true });
            }}
          />
          {actionType === 'panel' && (
            <MDSButton onClick={handleCommentSubmit} isDisabled={!isReadyToSubmit}>
              Comment
            </MDSButton>
          )}
        </Styled.PanelAction>
      </Styled.Editor>
      {actionType === 'dialog' && (
        <Styled.DialogAction>
          <MDSButton variant="border" color="bluegray" onClick={onCancel} isDisabled={addCommentMutation.isLoading}>
            Cancel
          </MDSButton>
          <MDSButton onClick={handleCommentSubmit} isDisabled={!isReadyToSubmit}>
            {appInfo.ctaLabel || 'Add comment'}
          </MDSButton>
        </Styled.DialogAction>
      )}
    </>
  );
};

const useGetTextAreaHeight = (fileListRef: React.RefObject<HTMLDivElement>, deps: any) => {
  const [textAreaMaxHeight, setTextAreaMaxHeight] = useState(MAX_HEIGHT - 46);
  useLayoutEffect(() => {
    const fileList = fileListRef.current;
    if (!fileList) return;

    const textArea = fileList.previousElementSibling as HTMLTextAreaElement;
    if (!textArea) return;

    textArea.style.maxHeight = MAX_HEIGHT - 46 - fileList.clientHeight + 'px';
    setTextAreaMaxHeight(MAX_HEIGHT - 46 - fileList.clientHeight);

    // 의도한 내용
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(deps)]);

  return textAreaMaxHeight;
};
